import { useCallback, useEffect, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useSelector } from "react-redux";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "./dropdownStyles.js";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule.js";
import moment from "moment";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";

function AddStaff() {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const CompID = authState.user?.userData?.compID;
  const { profileData } = authState;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [regularHours, setRegularHours] = useState(true);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    {
      from: "00:00",
      to: "23:59",
    },
  ]);
  const [commonBreak, setCommonBreak] = useState(null);

  const [schedules, setSchedules] = useState(schedule);

  const [staffData, setStaffData] = useState({
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    city: "",
    phoneNo: "",
    passCode: "",
    ratePerHour: null,
    active: false,
    userRoles: null,
    password: "",
    isAllowOverTime: false,
    isFullTimeStaff: false,
    isNewHireStaff: false,
    overTimeHourlyRate: null,
    isSalaryStaff: false,
    salaryType: "Weekly",
    salary: null,
  });

  const [errors, setErrors] = useState({});

  const validateCompensation = (value, isFullTime, isRequired = false) => {
    if (value === null || value === undefined || value === "") {
      if (isRequired) {
        return isFullTime ? "Salary is required" : "Hourly rate is required";
      }
      return "";
    }
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue) || numericValue <= 0) {
      return isFullTime ? "Enter a valid monthly salary" : "Enter a valid hourly rate";
    }
    return "";
  };

  const [imageState, setImageState] = useState({
    staffImage: "",
    imageFile: null,
  });

  const FetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID,
        },
      });
      const roleData = response.data.data || response.data;

      const rolesOptions = roleData.map((role) => ({
        value: role.comRolePermission_ID,
        label: role.r_Title,
      }));
      setRoles(rolesOptions);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showErrorToast("Failed to fetch roles");
    }
  }, [CompID]);

  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({
        ...slot,
        break: commonBreak
          ? {
              startBraekTime: commonBreak.startBraekTime,
              breakMinutes: commonBreak.breakMinutes,
              isBreakInclusive: commonBreak.isBreakInclusive || false,
            }
          : null,
      })),
    }));
    setSchedules(updatedSchedules);
  };

  // Ensure default schedules have break: null
  useEffect(() => {
    const defaultSchedulesWithBreaks = schedule.map((sch) => ({
      ...sch,
      timeSlots: sch.timeSlots.map((slot) => ({ ...slot, break: null })),
    }));
    setSchedules(defaultSchedulesWithBreaks);
  }, []);

  useEffect(() => {
    FetchRoles();
  }, [FetchRoles]);

  useEffect(() => {
    const defaultCountry =
      profileData?.compCountry ||
      profileData?.CompCountry ||
      "UK";
    const defaultCity =
      profileData?.compCity ||
      profileData?.CompCity ||
      "";

    setStaffData((prev) => {
      const updated = { ...prev };
      if (!updated.country && defaultCountry) {
        updated.country = defaultCountry;
      }
      if (!updated.city && defaultCity) {
        updated.city = defaultCity;
      }
      return updated;
    });
  }, [profileData]);

  const validateUsername = (value) => {
    if (!value.trim()) return "Username is required";
    if (value.length < 3) return "Username must be at least 3 characters long";
    return "";
  };

  const validateFirstName = (value) => {
    if (!value.trim()) return "First name is required";
    if (value.length < 2)
      return "First name must be at least 2 characters long";
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return "";
  };

  const validatePassword = (value) => {
    if (!value.trim()) return "Password is required";
    if (value.length < 6) return "Password must be at least 6 characters long";
    if (!/[!@#$%^&*]/.test(value))
      return "Password must contain at least one special character";
    if (!/\d/.test(value)) return "Password must contain at least one digit";
    if (!/[A-Z]/.test(value))
      return "Password must contain at least one uppercase letter";
    return "";
  };

  const validateCity = (value) => {
    if (!value || !value.toString().trim()) return "City is required";
    return "";
  };

  const validatePassCode = (value) => {
    if (!value.trim()) return "Pass code is required";
    return "";
  };

  const validateRole = (value) => {
    if (!value) return "User role is required";
    return "";
  };

  const validateOverTimeRate = (value, isAllowOverTime, ratePerHour) => {
    if (!isAllowOverTime) return "";
    let numValue = value ? parseFloat(value) : null;
    if (numValue === null) {
      numValue = ratePerHour ? parseFloat(ratePerHour) : null;
    }
    if (!numValue || numValue <= 0)
      return "Overtime rate must be a positive number";
    return "";
  };

  const validateSalary = (value, isSalaryStaff) => {
    if (!isSalaryStaff) return "";
    if (!value || parseFloat(value) <= 0)
      return "Salary must be a positive number";
    return "";
  };

  const validateSalaryType = (value, isSalaryStaff) => {
    if (!isSalaryStaff) return "";
    if (!value || value.trim() === "")
      return "Salary type is required";
    return "";
  };

  const handleInputChange = (
    field,
    value,
    validator,
    extraValidator = null
  ) => {
    setStaffData((prev) => ({ ...prev, [field]: value }));

    setErrors((prev) => ({ ...prev, [field]: "" }));

    let error = validator ? validator(value) : "";
    if (extraValidator) {
      const extraError = extraValidator(value);
      if (extraError) error = extraError;
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleSelectChange = (field, value, validator) => {
    let error = validator ? validator(value) : "";
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleCountryCityChange = (updatedValues) => {
    setStaffData((prev) => {
      const updated = { ...prev };
      if (updatedValues?.country !== undefined && updatedValues.country !== "") {
        updated.country = updatedValues.country;
      }
      if (updatedValues?.city !== undefined) {
        updated.city = updatedValues.city || "";
      }
      return updated;
    });

    if (updatedValues?.city !== undefined) {
      setErrors((prev) => ({
        ...prev,
        city: validateCity(updatedValues.city || ""),
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Username validation removed - will be auto-set from email
    newErrors.firstName = validateFirstName(staffData.firstName);
    newErrors.email = validateEmail(staffData.email);
    newErrors.password = validatePassword(staffData.password);
    newErrors.city = validateCity(staffData.city);
    newErrors.passCode = validatePassCode(staffData.passCode);
    newErrors.userRoles = validateRole(selectedRole?.value);

    // Status & Salary Info: only validate fields that are visible / applicable
    if (staffData.isSalaryStaff && staffData.salaryType === "Hourly") {
      newErrors.ratePerHour = validateCompensation(
        staffData.ratePerHour,
        false,
        true
      );
    }
    if (staffData.isSalaryStaff) {
      newErrors.salary = validateSalary(staffData.salary, true);
      newErrors.salaryType = validateSalaryType(staffData.salaryType, true);
    }
    if (staffData.isAllowOverTime) {
      newErrors.overTimeHourlyRate = validateOverTimeRate(
        staffData.overTimeHourlyRate,
        true,
        staffData.ratePerHour
      );
    }

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some(
      (error) => typeof error === "string" && error !== ""
    );
    if (hasErrors) {
      showErrorToast("Please fix the errors below before submitting");
      return false;
    }

    return true;
  };

  const handleImageChange = (url, file) => {
    setImageState({
      staffImage: url,
      imageFile: file,
    });
  };

  const HandleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    let {
      userName,
      firstName,
      lastName,
      email,
      country,
      city,
      phoneNo,
      passCode,
      ratePerHour,
      active,
      password,
      isAllowOverTime,
      isFullTimeStaff,
      isNewHireStaff,
      overTimeHourlyRate,
      isSalaryStaff,
      salaryType,
      salary,
    } = staffData;

    // Auto-set username from email
    userName = email;

    if (
      isAllowOverTime &&
      (!overTimeHourlyRate || overTimeHourlyRate === null)
    ) {
      overTimeHourlyRate = ratePerHour ? parseFloat(ratePerHour) : 0;
    }

    if (!country) {
      country =
        profileData?.compCountry ||
        profileData?.CompCountry ||
        "UK";
    }

    if (!city) {
      city =
        profileData?.compCity ||
        profileData?.CompCity ||
        "";
    }

    try {
      setLoading(true);
      let finalStaffImage = null;

      if (imageState.imageFile) {
        const imageFormData = new FormData();
        imageFormData.append("ImageFile", imageState.imageFile);
        imageFormData.append("UserName", userName);
        imageFormData.append("CompID", CompID);

        const imageResponse = await axiosInstance.post(
          APIs.UPDATE_STAFF_IMAGE,
          imageFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            params: {
              UserName: userName,
              CompID: CompID,
            },
          }
        );
        finalStaffImage = imageResponse.data?.data?.filePath || null;

        if (finalStaffImage) {
          const displayUrl = `${finalStaffImage}?t=${Date.now()}`;
          setImageState({ staffImage: displayUrl, imageFile: null });
        }
      } else if (!imageState.staffImage || imageState.staffImage === "") {
        finalStaffImage = null;
      } else {
        finalStaffImage = imageState.staffImage.split("?")[0];
      }

      const formattedSchedules = [];
      if (!regularHours) {
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          commonTimeSlot.forEach((slot) => {
            if (
              moment(slot.from, "HH:mm").isValid() &&
              moment(slot.to, "HH:mm").isValid()
            ) {
              days.forEach((day) => {
                formattedSchedules.push({
                  ID: 0,
                  CompID: CompID,
                  UserName: userName,
                  DayName: day,
                  OpenTime: moment(slot.from, "HH:mm").format("HH:mm"),
                  CloseTime: moment(slot.to, "HH:mm").format("HH:mm"),
                  StartBraekTime: commonBreak?.startBraekTime
                    ? commonBreak.startBraekTime
                    : null,
                  EndBraekTime:
                    commonBreak?.startBraekTime && commonBreak.breakMinutes
                      ? moment(commonBreak.startBraekTime, "HH:mm")
                          .add(commonBreak.breakMinutes, "minutes")
                          .format("HH:mm")
                      : null,
                  BreakMinutes: commonBreak?.breakMinutes || null,
                  IsBreakInclusive: commonBreak?.isBreakInclusive || false,
                  UpdateDateTime: new Date().toISOString(),
                });
              });
            }
          });
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (
                  moment(slot.from, "HH:mm").isValid() &&
                  moment(slot.to, "HH:mm").isValid()
                ) {
                  formattedSchedules.push({
                    ID: 0,
                    CompID: CompID,
                    UserName: userName,
                    DayName: schedule.day,
                    OpenTime: moment(slot.from, "HH:mm").format("HH:mm"),
                    CloseTime: moment(slot.to, "HH:mm").format("HH:mm"),
                    StartBraekTime: slot.break?.startBraekTime || null,
                    EndBraekTime:
                      slot.break?.startBraekTime && slot.break.breakMinutes
                        ? moment(slot.break.startBraekTime, "HH:mm")
                            .add(slot.break.breakMinutes, "minutes")
                            .format("HH:mm")
                        : null,
                    BreakMinutes: slot.break?.breakMinutes || null,
                    IsBreakInclusive: slot.break?.isBreakInclusive || false,
                    UpdateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
      }

      const isHourlySalary =
        isSalaryStaff && salaryType === "Hourly";

      const response = await axiosInstance.post(APIs.ADD_STAFF, {
        UserName: userName,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Country: country,
        City: city,
        PhoneNo: phoneNo,
        PassCode: passCode,
        RatePerHour:
          isHourlySalary && ratePerHour !== null
            ? parseFloat(ratePerHour)
            : null,
        Active: active,
        Password: password,
        CompID: CompID,
        ComRolePermission_ID: selectedRole.value,
        ManagerUserName: "",
        IsAllowOverTime: isAllowOverTime,
        ISFullTimeStaff: true,
        ISNewHireStaff: isNewHireStaff,
        OverTimeHourlyRate: overTimeHourlyRate
          ? parseFloat(overTimeHourlyRate)
          : 0,
        IsSalaryStaff: isSalaryStaff,
        SalaryType: isSalaryStaff ? salaryType : "",
        Salary:
          isSalaryStaff &&
          salaryType !== "Hourly" &&
          salary !== null
            ? parseFloat(salary)
            : null,
        WeeklySchedule: formattedSchedules,
        StaffImage: finalStaffImage || null,
      });

      setLoading(false);
      if (response.data.status) {
        showSuccessToast("Staff added successfully!");
        navigate("/team/manageTeam");
      } else {
        showErrorToast(response.data.message || "Failed to add staff");
      }
    } catch (error) {
      setLoading(false);
      const errorMessage =
        error.response?.data?.message ||
        (Array.isArray(error.response?.data?.errors)
          ? error.response.data.errors.join(", ")
          : "An error occurred while adding the staff");
      showErrorToast(errorMessage);
    }
  };

  const cardStyle = {
    backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
    border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
    color: isDarkMode ? "#ffffff" : "#000000",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
  };

  const sectionHeaderStyle = {
    color: isDarkMode ? "#ffffff" : "#495057",
    fontSize: "1.1rem",
    fontWeight: "600",
    marginBottom: "1.5rem",
    paddingBottom: "0.5rem",
    borderBottom: `2px solid ${themeColor}`,
  };

  // Error message style
  const errorStyle = {
    color: "#dc3545",
    fontSize: "0.875rem",
    marginTop: "0.25rem",
    fontWeight: "400",
  };

  const rateFieldLabel = "Hourly Rate";
  const rateFieldPlaceholder = "Enter Hourly Rate";

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/team/manageTeam">Manage Staff</Link>
            </li>
            <li className="breadcrumb-item active">Add New Staff</li>
          </ol>
          <h1 className="page-header mb-0">Add New Staff</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={() => HandleSubmit()}
            className="btn px-4"
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Save
          </button>
        </div>
      </div>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Staff Data</PanelHeader>
          <PanelBody>
            <div className="row g-4">
              {/* Basic Information Card */}
              <div className="col-md-6">
                <div className="p-4" style={cardStyle}>
                  
                  <div className="row g-3">
                    {/* Username field hidden - will be auto-set from email */}
                    <div className="col-12">
                      <FormInput
                        label="First Name *"
                        placeHolder="Enter First Name"
                        type="text"
                        value={staffData.firstName}
                        onchange={(e) =>
                          handleInputChange(
                            "firstName",
                            e.target.value,
                            validateFirstName
                          )
                        }
                      />
                      {errors.firstName && (
                        <div style={errorStyle}>{errors.firstName}</div>
                      )}
                    </div>
                    <div className="col-12">
                      <FormInput
                        label="Last Name"
                        placeHolder="Enter Last Name"
                        type="text"
                        value={staffData.lastName}
                        onchange={(e) =>
                          setStaffData({
                            ...staffData,
                            lastName: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="col-12">
                      <FormInput
                        label="Email *"
                        placeHolder="Enter Email"
                        type="email"
                        value={staffData.email}
                        onchange={(e) =>
                          handleInputChange(
                            "email",
                            e.target.value,
                            validateEmail
                          )
                        }
                      />
                      {errors.email && (
                        <div style={errorStyle}>{errors.email}</div>
                      )}
                    </div>
                    <div className="col-12">
                      <FormInput
                        label="Password *"
                        placeHolder="Enter Password"
                        type="password"
                        value={staffData.password}
                        onchange={(e) =>
                          handleInputChange(
                            "password",
                            e.target.value,
                            validatePassword
                          )
                        }
                      />
                      {errors.password && (
                        <div style={errorStyle}>{errors.password}</div>
                      )}
                    </div>
                    <div className="col-12">
                      <FormInput
                        label="Cell Number"
                        placeHolder="Enter Cell Number"
                        type="text"
                        value={staffData.phoneNo}
                        onchange={(e) =>
                          setStaffData({
                            ...staffData,
                            phoneNo: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Image & Status Section */}
              <div className="col-md-6">
                <div className="p-4 d-flex flex-column gap-3" style={cardStyle}>
                  <div className="text-center">
                    <ImageUpload
                      imageUrl={imageState.staffImage}
                      imageFile={imageState.imageFile}
                      setImage={handleImageChange}
                      className=""
                      centerButton={true}
                      useGrid={false}
                    />
                  </div>
                  <div>
                  
                    <div className="row g-3">
                      <div className="col-md-6 col-12">
                        <CountryCitySelector
                          selectedCountry={staffData.country}
                          selectedCity={staffData.city}
                          setSelectedCity={(city) => {
                            const value = city || "";
                            setStaffData((prev) => ({ ...prev, city: value }));
                            setErrors((prev) => ({
                              ...prev,
                              city: validateCity(value),
                            }));
                          }}
                          onChange={handleCountryCityChange}
                          setLoading={null}
                          showCountryField={false}
                          showCityField={true}
                          cityLabel="City *"
                          cityPlaceholder="Select City"
                          isDarkMode={isDarkMode}
                          selectedValueIsLabel={true}
                        />
                        {errors.city && (
                          <div style={errorStyle}>{errors.city}</div>
                        )}
                      </div>
                      <div className="col-md-6 col-12">
                        <label
                          className={isDarkMode ? "text-white" : "text-gray-700"}
                          style={{ fontWeight: "500" }}
                        >
                          User Roles *
                        </label>
                        <Select
                          value={selectedRole}
                          onChange={(option) => {
                            const newValue = option ? option.value : null;
                            setSelectedRole(option);
                            setStaffData({ ...staffData, userRoles: newValue });
                            handleSelectChange(
                              "userRoles",
                              newValue,
                              validateRole
                            );
                          }}
                          options={roles}
                          styles={dropdownStyles}
                          placeholder="Select Role"
                          isClearable={true}
                        />
                        {errors.userRoles && (
                          <div style={errorStyle}>{errors.userRoles}</div>
                        )}
                      </div>
                      <div className="col-12">
                        <FormInput
                          label="PIN Code *"
                          placeHolder="Enter PIN Code"
                          type="text"
                          value={staffData.passCode}
                          onchange={(e) =>
                            handleInputChange(
                              "passCode",
                              e.target.value,
                              validatePassCode
                            )
                          }
                        />
                        {errors.passCode && (
                          <div style={errorStyle}>{errors.passCode}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status & Compensation */}
              <div className="col-12">
                <div className="p-4" style={cardStyle}>
                  <h6 style={sectionHeaderStyle}>Status & Salary Info</h6>
                  <div className="row g-3 align-items-center mb-2">
                    {/* Row 1: Enable Staff */}
                    <div className="col-md-4 col-sm-6 col-12">
                      <ToggleSwitch
                        isChecked={staffData.active}
                        onToggle={(checked) =>
                          setStaffData({ ...staffData, active: checked })
                        }
                        label="Enable Staff"
                        name="active"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  
                  <div className="row g-3 align-items-center mb-2">
                    {/* Row 2: Salary Staff | Hourly Rate (if not salary staff) OR Salary Type & Salary (if salary staff) */}
                    <div className="col-md-4 col-sm-6 col-12">
                      <ToggleSwitch
                        isChecked={staffData.isSalaryStaff}
                        onToggle={(checked) => {
                          setStaffData({
                            ...staffData,
                            isSalaryStaff: checked,
                            ratePerHour: checked ? staffData.ratePerHour : null,
                            salary: checked ? staffData.salary : null,
                            salaryType: checked ? staffData.salaryType : "Weekly",
                          });
                          setErrors((prev) => ({
                            ...prev,
                            ratePerHour: "",
                            salary: "",
                            salaryType: "",
                          }));
                        }}
                        label="Salary Staff"
                        name="salaryStaff"
                        themeColor={themeColor}
                      />
                    </div>
                    {/* Salary Type - shown when Salary Staff is enabled */}
                    {staffData.isSalaryStaff && (
                      <div className="col-md-4 col-sm-6 col-12">
                        <label
                          className={isDarkMode ? "text-white" : "text-gray-700"}
                          style={{ fontWeight: "500" }}
                        >
                          Salary Type *
                        </label>
                        <Select
                          value={
                            staffData.salaryType
                              ? { value: staffData.salaryType, label: staffData.salaryType }
                              : null
                          }
                          onChange={(option) => {
                            const newValue = option ? option.value : "Weekly";
                            setStaffData({ ...staffData, salaryType: newValue });
                            handleSelectChange(
                              "salaryType",
                              newValue,
                              (val) => validateSalaryType(val, staffData.isSalaryStaff)
                            );
                          }}
                          options={[
                            { value: "Weekly", label: "Weekly" },
                            { value: "Monthly", label: "Monthly" },
                            { value: "Hourly", label: "Hourly" },
                          ]}
                          styles={dropdownStyles}
                          placeholder="Select Salary Type"
                          isClearable={false}
                        />
                        {errors.salaryType && (
                          <div style={errorStyle}>{errors.salaryType}</div>
                        )}
                      </div>
                    )}
                    {/* Hourly Rate - shown when Salary Type is Hourly */}
                    {staffData.isSalaryStaff &&
                      staffData.salaryType === "Hourly" && (
                      <div className="col-md-4 col-sm-6 col-12">
                        <FormInput
                          label={rateFieldLabel}
                          placeHolder={rateFieldPlaceholder}
                          type="number"
                          step="0.01"
                          value={
                            staffData.ratePerHour !== null &&
                            staffData.ratePerHour !== undefined
                              ? staffData.ratePerHour
                              : ""
                          }
                          onchange={(e) => {
                            const rawValue = e.target.value;
                            const parsedValue = rawValue
                              ? parseFloat(rawValue)
                              : null;
                            setStaffData((prev) => {
                              const updated = {
                                ...prev,
                                ratePerHour: parsedValue,
                              };
                              setErrors((prevErrors) => ({
                                ...prevErrors,
                                ratePerHour: validateCompensation(
                                  rawValue,
                                  false
                                ),
                              }));
                              return updated;
                            });
                          }}
                        />
                      </div>
                    )}
                    {/* Salary Field - shown when Salary Staff is enabled */}
                    {staffData.isSalaryStaff &&
                      staffData.salaryType !== "Hourly" && (
                      <div className="col-md-4 col-sm-6 col-12">
                        <FormInput
                          label={`${staffData.salaryType} Salary *`}
                          placeHolder={`Enter ${staffData.salaryType} Salary`}
                          type="number"
                          step="0.01"
                          value={staffData.salary || ""}
                          onchange={(e) =>
                            handleInputChange(
                              "salary",
                              e.target.value ? parseFloat(e.target.value) : null,
                              null,
                              () => validateSalary(
                                e.target.value ? parseFloat(e.target.value) : null,
                                staffData.isSalaryStaff
                              )
                            )
                          }
                        />
                        {errors.salary && (
                          <div style={errorStyle}>{errors.salary}</div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="row g-3 align-items-center">
                    {/* Row 3: Enable Extra Hours | Overtime Rate */}
                    <div className="col-md-4 col-sm-6 col-12">
                      <ToggleSwitch
                        isChecked={staffData.isAllowOverTime}
                        onToggle={(checked) => {
                          setStaffData({
                            ...staffData,
                            isAllowOverTime: checked,
                            overTimeHourlyRate: checked
                              ? staffData.overTimeHourlyRate || null
                              : null,
                          });
                          if (!checked) {
                            setErrors((prev) => ({
                              ...prev,
                              overTimeHourlyRate: "",
                            }));
                          }
                        }}
                        label="Enable Extra Hours"
                        name="allowOvertime"
                        description="Enable extra hours"
                        themeColor={themeColor}
                      />
                    </div>
                    {/* Overtime Rate - shown when Extra Hours is enabled */}
                    {staffData.isAllowOverTime && (
                      <div className="col-md-4 col-sm-6 col-12">
                        <FormInput
                          label="Hourly Rate for Extra Hours"
                          placeHolder="Enter Hourly Rate for Extra Hours"
                          type="number"
                          step="0.01"
                          value={staffData.overTimeHourlyRate || ""}
                          onchange={(e) =>
                            handleInputChange(
                              "overTimeHourlyRate",
                              e.target.value
                                ? parseFloat(e.target.value)
                                : null,
                              null,
                              () =>
                                validateOverTimeRate(
                                  e.target.value
                                    ? parseFloat(e.target.value)
                                    : null,
                                  staffData.isAllowOverTime,
                                  staffData.ratePerHour
                                )
                            )
                          }
                        />
                        {errors.overTimeHourlyRate && (
                          <div style={errorStyle}>
                            {errors.overTimeHourlyRate}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Weekly Schedule Card 

              <div className="col-12">
                <div className="p-4" style={cardStyle}>
                  <h6 style={sectionHeaderStyle}>Staff Schedule</h6>
                  <div className="row mb-3">
                    <div className="col-8">
                      <ToggleSwitch
                        isChecked={regularHours}
                        onToggle={(checked) => setRegularHours(checked)}
                        label={"Official Business Hours"}
                        disabled={false}
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  {!regularHours && (
                    <div className="row m-3">
                      <div className="mb-3">
                        <ToggleSwitch
                          isChecked={useSameTimeSlots}
                          onToggle={(checked) => {
                            setUseSameTimeSlots(checked);
                            if (!checked) {
                              applyCommonTimeSlotToAll();
                            }
                          }}
                          label={"Use same time slots for all days"}
                          disabled={false}
                          themeColor={themeColor}
                        />
                      </div>
                      {useSameTimeSlots ? (
                        <CommonTimeSlotForAllWeek
                          setCommonTimeSlots={setCommonTimeSlot}
                          commonTimeSlots={commonTimeSlot}
                          isStaffSchedule={true}
                          commonBreak={commonBreak}
                          setCommonBreak={setCommonBreak}
                        />
                      ) : (
                        <ScheduleManager
                          schedules={schedules}
                          setSchedules={setSchedules}
                          themeColor={themeColor}
                          isDarkMode={isDarkMode}
                          isProfilePage={false}
                          isStaffSchedule={true}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              */}
            </div>
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
}

export default AddStaff;
